import { useAuth } from "@sal/authentication.react";
import {
	ACCOUNT_ID_PARAM,
	ACCOUNT_STORAGE_KEY,
	getActiveAccountId,
	setActiveAccountId
} from "code/activeAccount";
import { refreshCv2Session } from "code/sessionRefresh";
import { ACCOUNTS_KEY } from "hooks/accounts";
import { Account, isAccountApproved, isAccountSelectable } from "models/Account";
import type { ReactNode } from "react";
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSWRConfig } from "swr";

interface AccountContextValue {
	accounts: Account[];
	activeAccount: Account | null;
	isLoading: boolean;
	cv2NotEnabled: boolean;
	/**
	 * True when the resolved active account has not yet been approved (see ADR 0003). No
	 * per-account data (ActivePermissions, company-scoped fetches) is requested while this is
	 * true — `RequireCv2Access` renders the "pending approval" page instead of the app routes.
	 */
	activeAccountPending: boolean;
	setActiveAccount: (account: Account) => void;
}

const AccountContext = createContext<AccountContextValue>({
	accounts: [],
	activeAccount: null,
	isLoading: true,
	cv2NotEnabled: false,
	activeAccountPending: false,
	setActiveAccount: () => {}
});

export function AccountProvider({ children }: { children: ReactNode }) {
	const [accounts, setAccounts] = useState<Account[]>([]);
	const [activeAccount, setActiveAccountState] = useState<Account | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [cv2NotEnabled, setCv2NotEnabled] = useState(false);
	const [activeAccountPending, setActiveAccountPending] = useState(false);
	const { mutate } = useSWRConfig();
	const auth = useAuth();
	const [searchParams, setSearchParams] = useSearchParams();

	useEffect(() => {
		const urlAccountId = searchParams.get(ACCOUNT_ID_PARAM);
		const storedAccountId = localStorage.getItem(ACCOUNT_STORAGE_KEY);
		const seededId = getActiveAccountId(); // set by seedAccountIdFromUrlOrStorage() at boot

		fetch("api/Accounts")
			.then(async (accountsRes) => {
				let response = accountsRes;
				if (response.status === 401) {
					// SWA-A had already expired when this bootstrap call fired — e.g. a race with SAL's
					// own enableTokenRefreshOnLoad Refresh, or a tab resumed after the access token's
					// 1-hour lifetime. Recover via the same refresh flow SessionExpiryProvider uses for
					// sandfieldFetch-based 401s (raw fetch() here doesn't get that treatment
					// automatically), then retry once. If refresh itself fails the session is genuinely
					// dead; fall through with the original 401 response, which degrades exactly as
					// before (caught by the .catch() below).
					const recovered = await refreshCv2Session(auth);
					if (recovered) {
						response = await fetch("api/Accounts");
					}
				}

				if (response.status === 403 && response.headers.get("X-Cv2-Access") === "not-enabled") {
					setCv2NotEnabled(true);
					return;
				}

				const res: unknown = await response.json();
				const rawList = Array.isArray(res) ? res : [];
				// Defensively normalise `isApproved` to a strict boolean so every Account in state has
				// a known value even if the API omits it or sends something unexpected (anything but
				// an explicit true is treated as pending - fail-safe).
				const list: Account[] = rawList.map((a: Account) => ({
					...a,
					isApproved: a?.isApproved === true
				}));
				setAccounts(list);
				// Pre-populate the SWR cache so useGetAccounts() (ACCOUNTS_KEY) finds the data
				// immediately and skips a redundant HTTP request when AccountsAndRoles is visited.
				mutate(ACCOUNTS_KEY, list, false);
				if (list.length > 0) {
					// api/Accounts is active-only now, so `list` itself is the fallback tier for a
					// pending-only user landing on the pending-approval page.
					const selectableList = list.filter(isAccountSelectable);
					const initial =
						selectableList.find((a) => a.accountID === urlAccountId) ??
						selectableList.find((a) => a.accountID === storedAccountId) ??
						selectableList.find((a) => a.isDefault) ??
						selectableList[0] ??
						list.find((a) => a.accountID === urlAccountId) ??
						list.find((a) => a.accountID === storedAccountId) ??
						list[0];
					const initialApproved = isAccountApproved(initial);
					setActiveAccountState(initial);
					setActiveAccountPending(!initialApproved);
					setActiveAccountId(initial.accountID); // must precede fetch - sets X-Cv2-Account header
					localStorage.setItem(ACCOUNT_STORAGE_KEY, initial.accountID);

					if (initialApproved) {
						// Await the permissions fetch before returning so .finally() → setIsLoading(false)
						// doesn't fire until company permissions are in auth state. This keeps the
						// RequireCompanyAccess spinner up, preventing AuthenticatedRoute from seeing the
						// stale base-contact permissions deposited by SAL's enableTokenRefreshOnLoad Refresh.
						try {
							const permsRes = await fetch("api/Accounts/ActivePermissions");
							if (permsRes.status === 401) {
								// Same boot-time-expired-session recovery as the api/Accounts call above.
								// refreshCv2Session already re-fetches ActivePermissions (for the account
								// header set just above) and stores the result on success, so there's
								// nothing further to do here in that case.
								await refreshCv2Session(auth);
							} else if (permsRes.ok) {
								const perms = await permsRes.json();
								auth.storeUserDetails({
									...auth.user,
									contactID: perms.contactId,
									permissions: perms.permissions
								});
							}
						} catch {
							// graceful degradation: route guards will see base permissions; user can refresh
						}

						// Seed was wrong or absent — revalidate so SWR refetches with the correct account.
						if (initial.accountID !== seededId) {
							mutate(() => true, undefined, { revalidate: true });
						}
					}
					// Pending accounts have no per-account permissions or data: RequireCv2Access
					// renders the "pending approval" page under the base shell instead, so neither the
					// ActivePermissions fetch nor the SWR revalidation above run for this account.
				} else if (seededId) {
					// No accounts (onboarding user) but a stale seed exists — clear it so subsequent
					// requests don't carry a foreign account header the server would 403.
					setActiveAccountId(null);
					localStorage.removeItem(ACCOUNT_STORAGE_KEY);
				}
			})
			.catch((err) => {
				console.error("Error loading accounts", err);
			})
			.finally(() => {
				setIsLoading(false);
			});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [mutate]);

	// Keep ?aid= in sync with the active account (initial load and account switches).
	useEffect(() => {
		if (!activeAccount?.accountID) {
			return;
		}
		if (searchParams.get(ACCOUNT_ID_PARAM) === activeAccount.accountID) {
			return;
		}
		const next = new URLSearchParams(searchParams);
		next.set(ACCOUNT_ID_PARAM, activeAccount.accountID);
		setSearchParams(next, { replace: true });
	}, [activeAccount, searchParams, setSearchParams]);

	const setActiveAccount = useCallback((account: Account) => {
		// Switch accounts via a *full page navigation* to Home with the new ?aid=, rather than an
		// in-SPA account swap. Permissions are per-account and SAL's userHasPermission() reads straight
		// from localStorage, so swapping permissions live while a route guard for the current page is
		// still mounted races it straight to /NotFound. A hard navigation re-runs AccountProvider's
		// mount flow — fetch ActivePermissions under the RequireCompanyAccess spinner — at "/", a route
		// every account can access. Home then redirects onward if the new account allows it. Account
		// switching is infrequent, so the reload cost is acceptable for a guaranteed-correct result.
		// This applies equally to a pending account: the reload re-resolves it (honouring ?aid=) and
		// lands on the "pending approval" page instead of fetching per-account permissions.
		setActiveAccountId(account.accountID); // sets X-Cv2-Account header for the post-reload boot
		localStorage.setItem(ACCOUNT_STORAGE_KEY, account.accountID);
		window.location.hash = `/?${ACCOUNT_ID_PARAM}=${account.accountID}`;
		window.location.reload();
	}, []);

	return (
		<AccountContext.Provider
			value={{
				accounts,
				activeAccount,
				isLoading,
				cv2NotEnabled,
				activeAccountPending,
				setActiveAccount
			}}
		>
			{children}
		</AccountContext.Provider>
	);
}

export function useAccount(): AccountContextValue {
	return useContext(AccountContext);
}
